import React, { useCallback, useMemo, useRef, type ElementRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { router } from 'expo-router';
import { Eye, ClipboardCheck, ChevronRight, Check } from 'lucide-react-native';
import { useTheme, FONT_SIZES, SPACING, TYPOGRAPHY, CATEGORY_ICONS } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useAuthGateStore } from '../../store/authGateStore';
import { AdaptiveSheet, Button } from '../common';
import { USER_ROLE_META, UserRole } from '../../types';
import { ROLE_ICONS, ROLE_ORDER } from '../../constants/roleIcons';
import { PanelStack, usePanelStack } from '../layout/PanelStack';
import { RoleSelectionBody } from '../profile/RoleSelectionSheet';
import { UpgradeAccountBody } from '../auth/UpgradeAccountSheet';

/**
 * "Tour REDI" — opened from the avatar's user menu. Two sections:
 *   1. How it works — the 4 prep cards (unchanged).
 *   2. Who's in REDI — explains the 9 roles, highlights the current user's
 *      roles, and exposes a "Manage your roles" CTA (signed-up) or
 *      "Save your account to pick roles" (anonymous).
 *
 * Dismissing the sheet flips uiStore.hasSeenTour, which kills the pulsing
 * indicator on the avatar.
 *
 * Roles and upgrade used to mount as second, nested AdaptiveSheets here —
 * two dialogs stacked (panel-system.md's "Nested info sheets"). As of Phase
 * 3B they're PanelStack drill-ins inside this one sheet instead: pressing
 * "Manage your roles" / "Save your account" pushes a view onto the
 * PanelStack below rather than opening another sheet, so there's only ever
 * one dialog and typed input survives a back-and-forth. `RoleSelectionBody`
 * and `UpgradeAccountBody` are the same form bodies their own sheet
 * wrappers use elsewhere (profile, and every other `upgradeAccount` call
 * site) — only the host around them differs here.
 */
export function HowReDIWorksSheet() {
  const open = useUIStore((s) => s.activeSheet === 'howItWorks');
  const sheetOpenTick = useUIStore((s) => s.sheetOpenTick);
  const close = useUIStore((s) => s.closeSheet);
  const markTourSeen = useUIStore((s) => s.markTourSeen);

  const handleSheetDismissed = useCallback(() => {
    markTourSeen();
    if (useUIStore.getState().activeSheet === 'howItWorks') {
      close();
    }
  }, [markTourSeen, close]);

  return (
    <AdaptiveSheet
      isOpen={open}
      tick={sheetOpenTick}
      backdrop="light"
      onRequestClose={close}
      onDismiss={handleSheetDismissed}
      dialogWidth={560}>
      {/* Single direct scrollable child: with enableDynamicSizing, only a
          BottomSheet scrollable gets a bounded height once content exceeds
          maxDynamicContentSize — a nested plain ScrollView never scrolls
          and the bottom of the tour was unreachable on phones. PanelStack
          swaps its root/pushed view in place inside this one scrollable, so
          that constraint still holds with roles/upgrade as drill-ins. */}
      <AdaptiveSheet.ScrollView testID="tour-sheet" showsVerticalScrollIndicator={false}>
        <PanelStack root={<TourRoot />} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

function TourRoot() {
  const { colors } = useTheme();
  const close = useUIStore((s) => s.closeSheet);
  const markTourSeen = useUIStore((s) => s.markTourSeen);
  const showTabBar = useUIStore((s) => s.showTabBar);
  const user = useAuthStore((s) => s.user);
  const isAnon = user?.isAnonymous === true;
  // First-touch visitor: hasn't tapped "I'm a Fire Scout" or signed in yet.
  // The tour doubles as the pre-signup education surface for this state.
  const isSignedOut = user == null;
  const userRoles = useMemo(
    () => new Set<UserRole>((user?.roles ?? []) as UserRole[]),
    [user?.roles]
  );
  const { push } = usePanelStack();
  // Restores focus to this button when PanelStack pops roles/upgrade back
  // off (panel-system.md's focus-return rule), matching the People-style
  // triggerRef pattern in DefensePlanAssignForm.web.
  const manageRolesButtonRef = useRef<ElementRef<typeof TouchableOpacity>>(null);

  const handleFaqPress = useCallback(() => {
    showTabBar();
    markTourSeen();
    close();
    router.push('/profile/faq' as never);
  }, [showTabBar, markTourSeen, close]);

  // Opens with no stashed composer path — this is informational, not a
  // detour on the way to posting, so ObservationGuidanceSheet's Continue
  // just closes it. openSheet('observationGuidance') already replaces
  // 'howItWorks' as the active sheet (single-active), so this sheet's own
  // tab-bar hold releases the same way it would on a plain dismiss.
  const handleGuidancePress = useCallback(() => {
    markTourSeen();
    close();
    useUIStore.getState().setObservationGuidanceNext(null);
    useUIStore.getState().openSheet('observationGuidance');
  }, [markTourSeen, close]);

  // Completing roles or the upgrade used to end with everything closed —
  // opening either one closed the tour first, so by the time you saved,
  // the tour was already gone. Closing the tour here on completion keeps
  // that same end state now that it's a drill-in instead (the automatic
  // PanelStack back arrow still just pops back to this view without
  // closing anything, which is new but exercises no existing flow).
  const handleDrillInComplete = useCallback(() => {
    markTourSeen();
    close();
  }, [markTourSeen, close]);

  const handleManageRoles = useCallback(() => {
    if (isSignedOut) {
      // No account at all yet: the next step is the auth gate, which leads
      // with the free Fire Scout path. The auth gate stays a true modal
      // (panel-system.md's "these stay dialogs" list), so the tour closes
      // first rather than drilling in.
      close();
      useAuthGateStore.getState().open(() => {}, { message: 'Get started free as a Fire Scout' });
    } else if (isAnon) {
      push({
        key: 'upgrade',
        title: 'Save your account',
        triggerRef: manageRolesButtonRef,
        render: () => <UpgradeAccountBody visible onClose={handleDrillInComplete} />,
      });
    } else {
      push({
        key: 'roles',
        title: 'Pick your roles',
        triggerRef: manageRolesButtonRef,
        render: () => <RoleSelectionBody visible onClose={handleDrillInComplete} />,
      });
    }
  }, [isSignedOut, isAnon, close, push, handleDrillInComplete]);

  const cards: {
    icon: React.ComponentType<{ size: number; color: string }>;
    iconColor: string;
    title: string;
    body: string;
  }[] = [
    {
      icon: Eye,
      iconColor: colors.primary,
      title: 'Spot wildfire prep',
      body: 'REDI maps community wildfire preparation. Anyone can post what they see, no signup required.',
    },
    {
      icon: CATEGORY_ICONS.progress,
      iconColor: colors.success,
      title: 'Green is progress',
      body: 'Tap + to share completed work: brush clearance, defensible space, fire-resistant materials.',
    },
    {
      icon: CATEGORY_ICONS.risk,
      iconColor: colors.opportunity,
      title: 'Red is opportunity',
      body: 'Red marks work a neighbor or local vendor could take on.',
    },
    {
      icon: ClipboardCheck,
      iconColor: colors.primary,
      title: 'Built for insurance',
      body: 'Insurance companies build custom checklists for your area. Your photos link prep to coverage.',
    },
  ];

  return (
    <View style={styles.content}>
      <Text style={[styles.title, { color: colors.text }]}>Tour REDI</Text>
      <Text style={[styles.tagline, { color: colors.textSecondary }]}>
        Community Wildfire Defense. A free platform that engages the whole community in preparing
        for wildfire.
      </Text>

      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>How it works</Text>
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <View key={card.title} style={[styles.card, { borderBottomColor: colors.border }]}>
            <View style={styles.cardIcon}>
              <Icon size={18} color={card.iconColor} />
            </View>
            <View style={styles.cardText}>
              <Text style={[styles.cardTitle, { color: colors.text }]}>{card.title}</Text>
              <Text style={[styles.cardBody, { color: colors.textSecondary }]}>{card.body}</Text>
            </View>
          </View>
        );
      })}

      <Text style={[styles.sectionLabel, { color: colors.textSecondary, marginTop: SPACING.md }]}>
        Who&apos;s in REDI
      </Text>
      {ROLE_ORDER.map((roleId) => {
        const meta = USER_ROLE_META[roleId];
        const Icon = ROLE_ICONS[roleId];
        const isMine = userRoles.has(roleId);
        const phase2 = meta.phase >= 2;
        return (
          <View key={roleId} style={[styles.roleRow, { borderBottomColor: colors.border }]}>
            <View style={styles.roleIcon}>
              <Icon size={16} color={colors.textSecondary} />
            </View>
            <View style={styles.roleText}>
              <View style={styles.roleLabelRow}>
                <Text style={[styles.roleLabel, { color: colors.text }]}>{meta.label}</Text>
                {isMine && <Check size={14} color={colors.text} />}
                {phase2 && (
                  <Text style={[styles.comingSoon, { color: colors.textTertiary }]}>Soon</Text>
                )}
              </View>
              <Text style={[styles.roleBlurb, { color: colors.textSecondary }]}>{meta.blurb}</Text>
            </View>
          </View>
        );
      })}

      <View style={styles.rolesAction}>
        <Button
          ref={manageRolesButtonRef}
          title={
            isSignedOut
              ? 'Get started for free'
              : isAnon
                ? 'Save your account to pick roles'
                : 'Manage your roles'
          }
          onPress={handleManageRoles}
          variant={isSignedOut || isAnon ? 'primary' : 'outline'}
          fullWidth
          testID={isSignedOut ? 'tour-get-started' : 'tour-manage-roles'}
        />
      </View>

      <TouchableOpacity
        onPress={handleGuidancePress}
        style={[styles.faqLink, { borderColor: colors.divider }]}
        accessibilityRole="link"
        accessibilityLabel="What should I look for when posting an observation?"
        testID="tour-guidance-link">
        <Text style={[styles.faqLinkText, { color: colors.text }]}>What should I look for?</Text>
        <ChevronRight size={16} color={colors.textSecondary} />
      </TouchableOpacity>

      <TouchableOpacity
        onPress={handleFaqPress}
        style={[styles.faqLink, { borderColor: colors.divider }]}
        accessibilityRole="link"
        accessibilityLabel="Open frequently asked questions"
        testID="tour-faq-link">
        <Text style={[styles.faqLinkText, { color: colors.text }]}>More questions? See FAQs</Text>
        <ChevronRight size={16} color={colors.textSecondary} />
      </TouchableOpacity>
      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="tour-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  scrollSentinel: {
    height: 1,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  tagline: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.xs,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
    letterSpacing: 0.8,
    marginTop: SPACING.md,
    marginBottom: SPACING.xs,
  },
  // Hairline rows, not filled cards: the tour is a list of what REDI does,
  // and four tinted boxes in a row is the pre-Design-B grammar.
  card: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: SPACING.smd,
  },
  cardIcon: {
    marginTop: 2,
  },
  cardText: {
    flex: 1,
  },
  cardTitle: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
    marginBottom: 2,
  },
  cardBody: {
    ...TYPOGRAPHY.bodySmall,
  },
  roleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: SPACING.smd,
  },
  roleIcon: {
    marginTop: 2,
  },
  roleText: {
    flex: 1,
  },
  roleLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginBottom: 2,
  },
  roleLabel: {
    ...TYPOGRAPHY.label,
    fontFamily: TYPOGRAPHY.h4.fontFamily,
  },
  comingSoon: {
    ...TYPOGRAPHY.labelSmall,
    letterSpacing: 0.6,
  },
  roleBlurb: {
    ...TYPOGRAPHY.bodySmall,
  },
  rolesAction: {
    marginTop: SPACING.smd,
  },
  faqLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.smd,
    marginTop: SPACING.xs,
    borderTopWidth: 1,
    gap: SPACING.xs,
  },
  faqLinkText: {
    ...TYPOGRAPHY.label,
  },
});
